import axios, { type InternalAxiosRequestConfig } from "axios";
import { store } from "../redux/store";
import { axiosClient } from "./axiosClient";
import { API } from "./endpoints";
import {
  setAccessToken,
  forceLogoutLocal,
} from "../redux/slices/adminSlice";

interface AxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token as string);
    }
  });
  failedQueue = [];
};

export const setupAxiosInterceptors = () => {
  axiosClient.interceptors.request.use(
    (config: AxiosRequestConfigWithRetry) => {
      if (config._retry) return config;
      const token = store.getState().admin.accessToken;
      if (token) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );
  axiosClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config as AxiosRequestConfigWithRetry;

      // #13: Handle 403 deactivated account
      if (error.response?.status === 403) {
        store.dispatch(forceLogoutLocal());
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        originalRequest.url !== API.admin.refresh
      ) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return axiosClient(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const res = await axios.post(
            `${import.meta.env.VITE_API_BASE_URL}${API.admin.refresh}`,
            {},
            { withCredentials: true },
          );

          const newAccessToken = res.data.newAccessToken;
          store.dispatch(setAccessToken(newAccessToken));
          
          isRefreshing = false;
          processQueue(null, newAccessToken);

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

          return axiosClient(originalRequest);
        } catch (err) {
          isRefreshing = false;
          processQueue(err, null);
          // #20: Redirect to login when refresh fails
          store.dispatch(forceLogoutLocal());
          window.location.href = "/login";
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    },
  );
};
